import React, { Suspense, useEffect, useMemo, useState } from "react";
// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from "perfect-scrollbar";
import { Route, Switch, useLocation, useHistory } from "react-router-dom";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import FixedPlugin from "components/FixedPlugin/FixedPlugin.js";
import Box from "@mui/material/Box";
import routes from "routes.js";
import { DateConverter } from "utils/DateFormate";
import ScriptNotes from "views/ScriptNotes";
import ReactGA from "react-ga";
import useUserStore from "store/user";
import { useRolesContext } from "context/rolesContext";
import Spinner from "components/Spinner";
import { useQuery } from "@tanstack/react-query";
import { getEvents } from "api/all.api";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import HubIcon from "@mui/icons-material/Hub";
import DescriptionIcon from "@mui/icons-material/Description";
import useCookie from "hooks/useCookie";

let ps;

ReactGA.initialize(process.env.REACT_APP_GA_CODE);

const trackPage = (page) => {
  ReactGA.set({ page });
  ReactGA.pageview(page);
};

function Dashboard(props) {
  const [backgroundColor, setBackgroundColor] = useState("#e8ecec");
  const [activeColor, setActiveColor] = useState("info");
  const [todayEvents, setTodayEvents] = useState([]);
  const [weeklyEvents, setWeeklyEvents] = useState([]);
  const mainPanel = React.useRef();
  const location = useLocation();
  const { user } = useUserStore();
  const [jwt] = useCookie("principally");
  const roles = useRolesContext();
  const [newRoutes, setNewRoutes] = useState(routes);
  const allIcons = useMemo(
    () => [
      <AccountCircleIcon />,
      <HomeIcon />,
      <CalendarTodayIcon />,
      <PersonIcon />,
      <LibraryBooksIcon />,
      <AutoStoriesIcon />,
      <AutoStoriesIcon />,
      <HubIcon />,
      <DescriptionIcon />,
    ],
    []
  );
  const [icons, setIcons] = useState(allIcons);

  let history = useHistory();

  const [userRole, setUserRole] = useState([]);

  const { data: events } = useQuery({
    queryKey: ["events", "logged_out"],
    queryFn: getEvents,
    staleTime: 1000 * 60 * 60,
  });

  useEffect(() => {
    setUserRole(roles?.filter((role) => role?.title === user?.role));
  }, [roles, user]);

  useEffect(() => {
    const page = window.location.pathname + window.location.search;
    trackPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, window]);

  if (!jwt) {
    history.push("/");
  }

  useEffect(() => {
    if (
      user?.role === "superintendent" ||
      user?.roleType === "specialuser" ||
      user?.roleType === "specialsecretary"
    ) {
      setNewRoutes(routes?.filter((route) => route.title !== "Coaching"));
      setIcons(allIcons?.filter((_, index) => index !== 4));
    }

    // eslint-disable-next-line
  }, [user, allIcons]);

  useEffect(() => {
    if (user && !(user?.role === "admin" || user?.role === "assistprincipal")) {
      setNewRoutes(routes?.filter((route) => route.num !== 6));
      setIcons(allIcons?.filter((_, index) => index !== 5));
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (
      user &&
      !(
        user?.role === "subadmin" ||
        user?.role === "assistprincipal" ||
        user?.role === "secretary" ||
        user?.role === "admin"
      )
    ) {
      setNewRoutes((all) => all?.filter((route) => route.num !== 7));
      setIcons(allIcons?.filter((_, index) => index !== 6));
    }
    // eslint-disable-next-line
  }, [user, newRoutes]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "assistprincipal")
      setNewRoutes(
        routes.map((route) =>
          route?.num === 6
            ? {
                ...route,
                name: "Principal Scripts",
                title: "Principal Scripts",
              }
            : route
        )
      );
  }, [user]);

  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(mainPanel.current);
      document.body.classList.toggle("perfect-scrollbar-on");
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
        document.body.classList.toggle("perfect-scrollbar-on");
      }
    };
  });
  React.useEffect(() => {
    mainPanel.current.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [location]);

  useEffect(() => {
    setTodayEvents([]);
    setWeeklyEvents([]);

    if (events && events?.length)
      events.forEach((event) => {
        const date1 = new Date(event.start);
        const date2 = new Date(new Date().toLocaleDateString());
        const dateDiff = date1 - date2;
        const diffTime = dateDiff;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (DateConverter(date1) === DateConverter(date2)) {
          setTodayEvents((prev) => [
            ...prev,
            { title: event.title, Diff: diffDays },
          ]);
        } else if (diffDays > 0 && diffDays <= 7)
          setWeeklyEvents((prev) => [
            ...prev,
            { title: event.title, Diff: diffDays - 1 },
          ]);
      });
  }, [events]);

  const handleActiveClick = (color) => {
    setActiveColor(color);
  };
  const handleBgClick = (color) => {
    setBackgroundColor(color);
  };

  return (
    <Suspense fallback={<Spinner />}>
      <div className="wrapper">
        <Sidebar
          {...props}
          routes={newRoutes}
          bgColor={backgroundColor}
          activeColor={activeColor}
          icons={icons}
          role={user?.role}
        />
        <Box
          className="main-panel"
          ref={mainPanel}
          sx={{
            "& >.content": {
              height: "calc(100vh - 150px)",
              overflow: "auto",
            },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DemoNavbar
            {...props}
            todayEvents={todayEvents}
            weeklyEvents={weeklyEvents}
            routes={newRoutes}
          />
          <div className="content">
            <Switch>
              {newRoutes.map((prop, key) => {
                return (
                  <Route
                    path={prop.layout + prop.path}
                    component={prop.component}
                    key={key}
                    exact
                  />
                );
              })}
              {user?.role === "admin" ||
              user?.role === "assistprincipaly" ||
              user?.role === "superadmin" ||
              (userRole?.length && userRole[0].allowScript) ? (
                <Route
                  path={"/admin/scripts/document"}
                  component={ScriptNotes}
                  exact
                />
              ) : (
                <></>
              )}
            </Switch>
          </div>
          <Footer fluid />
        </Box>
        <FixedPlugin
          bgColor={backgroundColor}
          activeColor={activeColor}
          handleActiveClick={handleActiveClick}
          handleBgClick={handleBgClick}
        />
      </div>
    </Suspense>
  );
}

// </LoggedProvidor>
export default Dashboard;
